/**
 * useEntrance — SSR-visible scroll entrance for marketing sections.
 *
 * An initial "hidden" variant server-renders sections with inline opacity:0,
 * hiding content from crawlers, AI agents and no-JS readers. This hook
 * inverts the responsibility:
 *
 * - Server HTML (and the hydration render) shows the 'visible' variant —
 *   full content in the raw HTML, correct LCP for slow connections.
 * - After hydration, a layout effect flips the section to 'hidden' BEFORE
 *   the next paint (the hidden variants snap with duration 0), and the
 *   normal entrance animation to 'visible' plays when the section scrolls
 *   into view.
 * - prefers-reduced-motion keeps the section visible throughout.
 *
 * Usage:
 *   const { ref, entrance, inView } = useEntrance({ amount: 0.2 });
 *   <motion.div ref={ref} variants={...} initial={false} animate={entrance}>
 *
 * `initial={false}` is required: it makes framer-motion server-render the
 * `animate` state instead of a hidden initial state.
 */

'use client';

import { useEffect, useLayoutEffect, useRef, useState } from 'react';
import { useInView, useReducedMotion } from 'framer-motion';

// useLayoutEffect warns during SSR; useEffect is the server-side no-op stand-in.
const useIsomorphicLayoutEffect =
  typeof window !== 'undefined' ? useLayoutEffect : useEffect;

export interface UseEntranceOptions {
  /** Animate only on first scroll into view (default true) */
  once?: boolean;
  /** Fraction of element visible before triggering (default 0.2) */
  amount?: number;
}

export function useEntrance<T extends Element = HTMLDivElement>({
  once = true,
  amount = 0.2
}: UseEntranceOptions = {}) {
  const ref = useRef<T>(null);
  const prefersReduced = useReducedMotion();
  const inView = useInView(ref, { once, amount });
  const [hasMounted, setHasMounted] = useState(false);

  useIsomorphicLayoutEffect(() => {
    setHasMounted(true);
  }, []);

  const entrance: 'visible' | 'hidden' =
    !hasMounted || prefersReduced || inView ? 'visible' : 'hidden';

  return { ref, entrance, inView, prefersReduced, hasMounted };
}
